import {
  IonButton,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonProgressBar,
  IonRow,
  IonSpinner,
  IonText,
  IonToolbar,
} from '@ionic/react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from 'ag-grid-react';
import {
  alertCircleOutline,
  arrowBackOutline,
  checkmarkCircleOutline,
  helpCircleOutline,
  musicalNotesOutline,
  peopleOutline,
  pricetagsOutline,
  refreshOutline,
  statsChartOutline,
  copyOutline,
  calendarOutline,
  musicalNoteOutline,
  pulseOutline,
  playOutline,
  folderOutline,
  warningOutline,
  discOutline,
} from 'ionicons/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { IpcEventNames } from '../../../constants/ipcEventNames';
import { once, removeListener, run } from '../../../helper/electron';
import './LibrarySummaryStep.scss';

// Interface for the detailed statistics received from the backend
interface LibraryStatistics {
  totalFiles: number;
  totalArtists: number;
  totalAlbums: number;
  totalGenres: number;
  duplicates: number;
  topArtists: [string, number][];
  topGenres: [string, number][];
  topAlbums: [string, number][];
  lowBitrateCount: number;
  unplayedCount?: number;
  missingBpmCount?: number;
  missingKeyCount?: number;
  missingFileCount?: number;
  lowBitrateTracks?: any[];
  unplayedTrackList?: any[];
  missingBpmTrackList?: any[];
  missingKeyTrackList?: any[];
  missingFileTrackList?: any[];
  missingGenreTrackList: any[];
  missingYearTrackList: any[];
  missingTitleOrArtistTrackList: any[];
  duplicateTrackList: [];
  // New metrics
  hasSerato?: boolean;
  missingGenreCount?: number;
  missingYearCount?: number;
  missingTitleOrArtistCount?: number;
  exactDuplicateCount?: number;
}

interface TrackTags {
  title?: string;
  artist?: string | string[];
  album?: string;
  genre?: string | string[];
  year?: string | number;
  bpm?: string | number;
  key?: string;
  bitrate?: string | number;

  [key: string]: any; // allow extras
}

interface LibrarySummaryStepProps {
  onComplete: () => void;
  onViewDuplicatesOnFeed?: () => void;
  standalonePage?: boolean;
}

const ISSUE_PREVIEW_LIMIT = 5;

const LibrarySummaryStep: React.FC<LibrarySummaryStepProps> = ({
  onComplete,
  onViewDuplicatesOnFeed,
  standalonePage,
}) => {
  const [stats, setStats] = useState<LibraryStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [healthScore, setHealthScore] = useState<number>(0);
  const [showIssueGrid, setShowIssueGrid] = useState(false);
  const [activeIssueTitle, setActiveIssueTitle] = useState('');
  const [issueTracksForGrid, setIssueTracksForGrid] = useState<any[]>([]);
  const [issueGridRefreshKey, setIssueGridRefreshKey] = useState<number>(0);
  const [issueLoading, setIssueLoading] = useState<boolean>(false);
  const [currentGridColDefs, setCurrentGridColDefs] = useState<ColDef[]>([]);
  const history = useHistory();

  const listenerRefs = useRef<{
    [key: string]: ((...args: any[]) => void) | undefined;
  }>({});

  // Define all possible columns
  const allPossibleColDefs = useMemo<Record<string, ColDef>>(
    () => ({
      title: {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data.title || 'N/A',
      },
      artist: {
        field: 'artist',
        headerName: 'Artist',
        flex: 2,
        minWidth: 200,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data.artist || 'N/A',
      },
      filePath: {
        field: 'filePath',
        headerName: 'File Path',
        flex: 3,
        minWidth: 300,
        sortable: true,
        filter: true,
      },
      bitrate: {
        field: 'bitrate',
        headerName: 'Bitrate (kbps)',
        width: 150,
        sortable: true,
        filter: true,
        valueGetter: (params) =>
          params.data.bitrate ? `${params.data.bitrate} kbps` : 'N/A',
      },
      bpm: {
        field: 'bpm',
        headerName: 'BPM',
        width: 120,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data.bpm || 'N/A',
      },
      key: {
        field: 'key',
        headerName: 'Key',
        width: 120,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data.key || 'N/A',
      },
      playCount: {
        field: 'playCount',
        headerName: 'Play Count',
        width: 120,
        sortable: true,
        filter: true,
        valueGetter: (params) =>
          params.data.playCount !== undefined ? params.data.playCount : 'N/A',
      },
    }),
    [],
  );

  const generateColDefsForIssue = useCallback(
    (issueType: string): ColDef[] => {
      const baseCols = [
        allPossibleColDefs.title,
        allPossibleColDefs.artist,
        allPossibleColDefs.filePath,
      ];
      switch (issueType) {
        case 'lowBitrate':
          return [...baseCols, allPossibleColDefs.bitrate];
        case 'missingBpm':
          return [...baseCols, allPossibleColDefs.bpm];
        case 'missingKey':
          return [...baseCols, allPossibleColDefs.key];
        case 'unplayed':
          return [...baseCols, allPossibleColDefs.playCount];
        case 'missingFiles':
          return [
            allPossibleColDefs.filePath,
            allPossibleColDefs.title,
            allPossibleColDefs.artist,
          ];
        default:
          return baseCols;
      }
    },
    [allPossibleColDefs],
  );

  const fetchAndProcessStats = useCallback(() => {
    setIsLoading(true);

    if (listenerRefs.current.healthReport) {
      removeListener(
        'get-full-health-report-data',
        listenerRefs.current.healthReport,
      );
    }

    const handleFullReportReceived = (
      reportData: LibraryStatistics & {
        score: number;
        status: string;
        timestamp: number;
        unplayedCount: number /* other fields from generate */;
      },
    ) => {
      if (listenerRefs.current.healthReport) {
        removeListener(
          'get-full-health-report-data',
          listenerRefs.current.healthReport,
        );
        delete listenerRefs.current.healthReport;
      }

      if (reportData && typeof reportData.score === 'number') {
        setStats(reportData);
        setHealthScore(reportData.score);
        run('save-health-report-result', reportData);
      } else {
        setStats({
          totalFiles: 0,
          totalArtists: 0,
          totalAlbums: 0,
          totalGenres: 0,
          duplicates: 0,
          topArtists: [],
          topGenres: [],
          topAlbums: [],
          lowBitrateCount: 0,
          unplayedCount: 0,
          missingBpmCount: 0,
          missingKeyCount: 0,
          missingFileCount: 0,
          lowBitrateTracks: [],
          missingGenreTrackList: [],
          missingTitleOrArtistTrackList: [],
          missingYearTrackList: [],
          duplicateTrackList: [],
        });
        setHealthScore(0);
      }
      setIsLoading(false);
    };
    const eventName = 'get-full-health-report-data';
    listenerRefs.current.healthReport = handleFullReportReceived;
    once(eventName, handleFullReportReceived);
    run(eventName);
  }, []);

  useEffect(() => {
    fetchAndProcessStats();
    return () => {
      Object.values(listenerRefs.current).forEach((listener, index) => {
        const eventName = Object.keys(listenerRefs.current)[index];
        removeListener(eventName, listener);
      });
      listenerRefs.current = {};
    };
  }, [fetchAndProcessStats]);

  const getHealthColor = () => {
    if (healthScore >= 85) return 'success';
    if (healthScore >= 60) return 'warning';
    return 'danger';
  };

  const roundedHealthScore =
    healthScore !== null ? Math.round(healthScore) : null;

  const viewIssueTracks = useCallback(
    (title: string, issueType: string) => {
      setActiveIssueTitle(title);
      setIssueTracksForGrid([]);
      setCurrentGridColDefs(generateColDefsForIssue(issueType));
      setShowIssueGrid(true);
      setIssueLoading(true);

      const channel = IpcEventNames.ON_HEALTH_REPORT_GET_ISSUE_DETAILS;

      if (listenerRefs.current.issueDetails) {
        removeListener(channel, listenerRefs.current.issueDetails);
        delete listenerRefs.current.issueDetails;
      }

      const handler = (_event: any, payload: any) => {
        if (listenerRefs.current.issueDetails) {
          removeListener(channel, listenerRefs.current.issueDetails);
          delete listenerRefs.current.issueDetails;
        }

        if (!payload || payload.error) {
          console.error('Failed to load issue tracks:', payload?.error);
          setIssueLoading(false);
          return;
        }

        const normalizedTracks = (payload.tracks || []).map((track: any) => {
          let tags: TrackTags = {};
          try {
            tags =
              typeof track.tags === 'string'
                ? (JSON.parse(track.tags) as TrackTags)
                : (track.tags as TrackTags) || {};
          } catch (e) {
            console.warn('Invalid tags JSON for track', track.id, e);
          }

          return {
            id: track.id,
            filePath: track.filePath,
            title: tags.title || '',
            artist: Array.isArray(tags.artist)
              ? tags.artist.join(', ')
              : tags.artist || '',
            album: tags.album || '',
            genre: Array.isArray(tags.genre)
              ? tags.genre.join(', ')
              : tags.genre || '',
            year: tags.year || '',
            bpm: tags.bpm || '',
            key: tags.key || '',
            bitrate: tags.bitrate || '',
            playCount: track.seratoPlayCount || null,
          };
        });

        setIssueTracksForGrid(normalizedTracks);
        setIssueGridRefreshKey((key) => key + 1);
        setIssueLoading(false);
      };

      listenerRefs.current.issueDetails = handler;
      once(channel, handler);

      run(channel, { issueType });
    },
    [generateColDefsForIssue],
  );

  if (isLoading) {
    return (
      <div className="loading-container">
        <IonSpinner name="crescent" />
        <p>Analyzing Library Health...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="error-container">
        <IonIcon icon={alertCircleOutline} color="danger" />
        <p>Could not load library statistics.</p>
        <IonButton fill="outline" onClick={onComplete}>
          Go to Library
        </IonButton>
      </div>
    );
  }

  const severityStyles: Record<string, React.CSSProperties> = {
    critical: {
      background: 'rgba(239, 68, 68, 0.12)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.18)',
    },
    warning: {
      background: 'rgba(245, 158, 11, 0.12)',
      color: '#f59e0b',
      border: '1px solid rgba(245, 158, 11, 0.18)',
    },
    info: {
      background: 'rgba(59, 130, 246, 0.12)',
      color: '#3b82f6',
      border: '1px solid rgba(59, 130, 246, 0.18)',
    },
  };

  const issues = [
    {
      key: 'missingGenre',
      label: 'Tracks Missing Genre',
      count: stats.missingGenreCount || 0,
      issueType: 'missingGenre',
      route: null,
      tooltip:
        'Tracks without a genre tag make it harder to organize and find music by style.',
      icon: pricetagsOutline,
      severity: 'info',
    },
    {
      key: 'missingYear',
      label: 'Tracks Missing Year',
      count: stats.missingYearCount || 0,
      issueType: 'missingYear',
      route: null,
      tooltip:
        'Tracks without a year tag may be harder to sort by era or identify for sets.',
      icon: calendarOutline,
      severity: 'info',
    },
    {
      key: 'missingTitleOrArtist',
      label: 'Tracks Missing Title or Artist',
      count: stats.missingTitleOrArtistCount || 0,
      issueType: 'missingTitleOrArtist',
      route: null,
      tooltip:
        'Tracks missing a title or artist are difficult to identify and manage.',
      icon: peopleOutline,
      severity: 'critical',
    },
    {
      key: 'exactDuplicates',
      label: 'Exact File Duplicates',
      count: stats.exactDuplicateCount || 0,
      issueType: 'exactDuplicates',
      route: null,
      tooltip:
        'Exact file duplicates waste space and can cause confusion in your library.',
      icon: copyOutline,
      severity: 'critical',
    },
    {
      key: 'duplicates',
      label: 'Duplicate Tracks (Tag-based)',
      count: stats.duplicates,
      issueType: 'duplicates',
      route: '/resolve-duplicates',
      tooltip:
        'Duplicates clutter your crates and risk playing the same track twice—keeping only the best version frees space and streamlines your workflow.',
      icon: copyOutline,
      severity: 'critical',
    },
    {
      key: 'lowBitrate',
      label: 'Low Bitrate Tracks (<192kbps)',
      count: stats.lowBitrateCount,
      issueType: 'lowBitrate',
      route: null,
      tooltip:
        'Low-bitrate files sound dull and thin—upgrading to 320 kbps MP3s or WAVs keeps your set crisp and professional.',
      icon: warningOutline,
      severity: 'critical',
    },
    {
      key: 'missingBpm',
      label: 'Tracks Missing BPM',
      count: stats.missingBpmCount || 0,
      issueType: 'missingBpm',
      route: null,
      tooltip:
        'Without BPM data, filtering and syncing tracks takes longer—adding accurate BPM lets you automate your workflow and keep the energy steady.',
      icon: pulseOutline,
      severity: 'warning',
    },
    {
      key: 'missingKey',
      label: 'Tracks Missing Key',
      count: stats.missingKeyCount || 0,
      issueType: 'missingKey',
      route: null,
      tooltip:
        'Harmonic mixing relies on key info—tagging each track’s musical key helps you blend smoothly and maintain energy on the dancefloor.',
      icon: musicalNoteOutline,
      severity: 'warning',
    },
    // Serato-specific metrics (conditionally shown)
    ...(stats.hasSerato
      ? [
          {
            key: 'unplayed',
            label: 'Unplayed Tracks (Serato History)',
            count: stats.unplayedCount || 0,
            issueType: 'unplayed',
            route: null,
            tooltip:
              'Unplayed tracks often go forgotten—audition or archive them to focus on proven crowd-pleasers and rediscover hidden gems.',
            icon: playOutline,
            severity: 'info',
          },
          {
            key: 'missingFiles',
            label: 'Missing Music Files',
            count: stats.missingFileCount || 0,
            issueType: 'missingFiles',
            route: null,
            tooltip:
              'Broken or missing files are show-stoppers—relinking ensures every track is ready to go and prevents awkward silence mid-set.',
            icon: folderOutline,
            severity: 'critical',
          },
        ]
      : []),
  ];

  const visibleIssues = issues
    .filter((i) => i.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <div className="library-summary-step">
      {showIssueGrid ? (
        <div className="issue-grid-view ion-padding">
          <div className="issue-grid-header">
            <h3>{activeIssueTitle}</h3>
            <IonButton
              onClick={() => setShowIssueGrid(false)}
              fill="outline"
              size="small"
            >
              <IonIcon icon={arrowBackOutline} slot="start" />
              Back to Summary
            </IonButton>
          </div>
          {issueLoading ? (
            <div className="loading-container">
              <IonSpinner name="crescent" />
              <p>Loading tracks…</p>
            </div>
          ) : (
            <div
              className="ag-theme-material-dark"
              style={{ height: '60vh', width: '100%' }}
            >
              <AgGridReact
                key={issueGridRefreshKey}
                rowData={issueTracksForGrid}
                columnDefs={currentGridColDefs}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  filter: true,
                }}
                pagination={true}
                paginationPageSize={50}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Header Actions */}
          <IonToolbar className="actions-row">
            <IonButton onClick={fetchAndProcessStats} fill="clear" slot="start">
              <IonIcon icon={refreshOutline} slot="start" />
              Re-analyze
            </IonButton>
            <IonButton
              onClick={onComplete}
              fill="outline"
              color="primary"
              slot="end"
            >
              <IonIcon icon={checkmarkCircleOutline} slot="start" />
              View Library
            </IonButton>
          </IonToolbar>

          {/* Health Score Card */}
          <div className="health-score-section">
            <h3 className="health-score-title">Library Health Score</h3>
            <div className="score-display">
              <span className={`score-value color-${getHealthColor()}`}>
                {roundedHealthScore !== null ? roundedHealthScore : '—'}
              </span>
              <span className="score-max">/ 100</span>
            </div>
            <IonProgressBar
              value={(roundedHealthScore ?? 0) / 100}
              color={getHealthColor()}
              className="health-progress-bar-new"
            />
          </div>

          {/* Grid for Issues & Stats */}
          <IonGrid>
            <IonRow>
              <IonCol size="12" size-md="6">
                <div className="issues-card">
                  <IonCardHeader style={{ paddingBottom: '12px' }}>
                    <IonCardTitle
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '1.2rem',
                        fontWeight: 650,
                        color: 'rgba(255, 255, 255, 0.95)',
                      }}
                    >
                      <IonIcon
                        icon={alertCircleOutline}
                        style={{ fontSize: '1.35rem', color: '#ff6b6b' }}
                      />
                      Potential Issues
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent className="potential-issues-content">
                    <div
                      className="issues-list"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        width: '100%',
                      }}
                    >
                      {visibleIssues.map((issue) => (
                        <div
                          key={issue.key}
                          className="issue-item-row-simple"
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '10px 5px',
                            borderRadius: '12px',
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            textAlign: 'left',
                          }}
                        >
                          <div
                            className="issue-left-simple"
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: '12px',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <div
                              className={`issue-icon-box-simple severity-${issue.severity}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '34px',
                                height: '34px',
                                borderRadius: '8px',
                                fontSize: '1.15rem',
                                flexShrink: 0,
                                ...severityStyles[issue.severity],
                              }}
                            >
                              <IonIcon icon={issue.icon} />
                            </div>
                            <div
                              className="issue-info-simple"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                              }}
                            >
                              <span
                                className="issue-text-simple"
                                style={{
                                  fontSize: '0.95rem',
                                  fontWeight: 500,
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'left',
                                }}
                              >
                                {issue.count} {issue.label}
                              </span>
                            </div>
                          </div>
                          <div
                            className="issue-right-simple"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: '8px',
                              flexShrink: 0,
                            }}
                          >
                            <div
                              className="issue-help-wrapper-simple"
                              id={`hover-trigger-${issue.key}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255, 255, 255, 0.35)',
                                cursor: 'pointer',
                                padding: '6px',
                                borderRadius: '50%',
                                flexShrink: 0,
                              }}
                            >
                              <IonIcon
                                icon={helpCircleOutline}
                                className="issue-help-icon-simple"
                                style={{
                                  fontSize: '1.15rem',
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {visibleIssues.map((issue) => (
                      <IonPopover
                        key={`popover-${issue.key}`}
                        trigger={`hover-trigger-${issue.key}`}
                        triggerAction="hover"
                        className="issue-tooltip-popover"
                      >
                        <div className="issue-tooltip-content">
                          {issue.tooltip}
                        </div>
                      </IonPopover>
                    ))}
                  </IonCardContent>
                </div>
              </IonCol>

              <IonCol size="12" size-md="6">
                {/* Library Stats Card */}
                <div className="health-report-card">
                  <IonCardHeader style={{ paddingBottom: '12px' }}>
                    <IonCardTitle
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '1.2rem',
                        fontWeight: 650,
                        color: 'rgba(255, 255, 255, 0.95)',
                      }}
                    >
                      <IonIcon
                        icon={statsChartOutline}
                        style={{ fontSize: '1.35rem', color: '#60a5fa' }}
                      />
                      Library Stats
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent className="potential-issues-content">
                    <div
                      className="issues-list"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        width: '100%',
                      }}
                    >
                      {[
                        {
                          label: 'Total Tracks',
                          value: stats.totalFiles,
                          icon: musicalNotesOutline,
                        },
                        {
                          label: 'Unique Artists',
                          value: stats.totalArtists,
                          icon: peopleOutline,
                        },
                        {
                          label: 'Unique Albums',
                          value: stats.totalAlbums,
                          icon: discOutline,
                        },
                        {
                          label: 'Unique Genres',
                          value: stats.totalGenres,
                          icon: pricetagsOutline,
                        },
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="issue-item-row-simple"
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '10px 5px',
                            borderRadius: '12px',
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            textAlign: 'left',
                          }}
                        >
                          <div
                            className="issue-left-simple"
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: '12px',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <div
                              className="issue-icon-box-simple severity-info"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '34px',
                                height: '34px',
                                borderRadius: '8px',
                                fontSize: '1.15rem',
                                flexShrink: 0,
                                ...severityStyles.info,
                              }}
                            >
                              <IonIcon icon={item.icon} />
                            </div>
                            <div
                              className="issue-info-simple"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                              }}
                            >
                              <span
                                className="issue-text-simple"
                                style={{
                                  fontSize: '0.95rem',
                                  fontWeight: 500,
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'left',
                                }}
                              >
                                {item.label}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              color: '#60a5fa',
                              padding: '4px 8px',
                            }}
                          >
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </IonCardContent>
                </div>
              </IonCol>
            </IonRow>

            {/* Bottom Row: Top Lists */}
            <IonRow>
              <IonCol size="12" size-md="4">
                {/* Top Genres Card */}
                <div className="health-report-card">
                  <IonCardHeader style={{ paddingBottom: '12px' }}>
                    <IonCardTitle
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '1.15rem',
                        fontWeight: 650,
                        color: 'rgba(255, 255, 255, 0.95)',
                      }}
                    >
                      <IonIcon
                        icon={pricetagsOutline}
                        style={{ fontSize: '1.25rem', color: '#34d399' }}
                      />
                      Top Genres
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent
                    style={{ textAlign: 'left', padding: '12px' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {stats.topGenres.length ? (
                        stats.topGenres.map(([genre, count], i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.015)',
                              border: '1px solid rgba(255, 255, 255, 0.03)',
                              marginBottom: '6px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '10px',
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  fontSize: '0.8rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  background:
                                    i === 0
                                      ? 'linear-gradient(135deg, #ffd700, #ffa500)'
                                      : i === 1
                                        ? 'linear-gradient(135deg, #c0c0c0, #a9a9a9)'
                                        : i === 2
                                          ? 'linear-gradient(135deg, #cd7f32, #8b5a2b)'
                                          : 'rgba(255, 255, 255, 0.08)',
                                  color:
                                    i < 3 ? '#000' : 'rgba(255, 255, 255, 0.7)',
                                }}
                              >
                                {i + 1}
                              </span>
                              <span
                                style={{
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'left',
                                }}
                              >
                                {genre}
                              </span>
                            </div>
                            <span
                              style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: 'rgba(255, 255, 255, 0.65)',
                                flexShrink: 0,
                              }}
                            >
                              {count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '8px',
                            color: 'rgba(255, 255, 255, 0.45)',
                            fontSize: '0.9rem',
                          }}
                        >
                          No genre data found.
                        </div>
                      )}
                    </div>
                  </IonCardContent>
                </div>
              </IonCol>

              <IonCol size="12" size-md="4">
                {/* Top Artists Card */}
                <div className="health-report-card">
                  <IonCardHeader style={{ paddingBottom: '12px' }}>
                    <IonCardTitle
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '1.15rem',
                        fontWeight: 650,
                        color: 'rgba(255, 255, 255, 0.95)',
                      }}
                    >
                      <IonIcon
                        icon={peopleOutline}
                        style={{ fontSize: '1.25rem', color: '#a78bfa' }}
                      />
                      Top Artists
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent
                    style={{ textAlign: 'left', padding: '12px' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {stats.topArtists.length ? (
                        stats.topArtists.map(([artist, count], i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.015)',
                              border: '1px solid rgba(255, 255, 255, 0.03)',
                              marginBottom: '6px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '10px',
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  fontSize: '0.8rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  background:
                                    i === 0
                                      ? 'linear-gradient(135deg, #ffd700, #ffa500)'
                                      : i === 1
                                        ? 'linear-gradient(135deg, #c0c0c0, #a9a9a9)'
                                        : i === 2
                                          ? 'linear-gradient(135deg, #cd7f32, #8b5a2b)'
                                          : 'rgba(255, 255, 255, 0.08)',
                                  color:
                                    i < 3 ? '#000' : 'rgba(255, 255, 255, 0.7)',
                                }}
                              >
                                {i + 1}
                              </span>
                              <span
                                style={{
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'left',
                                }}
                              >
                                {artist}
                              </span>
                            </div>
                            <span
                              style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: 'rgba(255, 255, 255, 0.65)',
                                flexShrink: 0,
                              }}
                            >
                              {count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '8px',
                            color: 'rgba(255, 255, 255, 0.45)',
                            fontSize: '0.9rem',
                          }}
                        >
                          No artist data found.
                        </div>
                      )}
                    </div>
                  </IonCardContent>
                </div>
              </IonCol>

              <IonCol size="12" size-md="4">
                {/* Top Albums Card */}
                <div className="health-report-card">
                  <IonCardHeader style={{ paddingBottom: '12px' }}>
                    <IonCardTitle
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '1.15rem',
                        fontWeight: 650,
                        color: 'rgba(255, 255, 255, 0.95)',
                      }}
                    >
                      <IonIcon
                        icon={musicalNotesOutline}
                        style={{ fontSize: '1.25rem', color: '#fb7185' }}
                      />
                      Top Albums
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent
                    style={{ textAlign: 'left', padding: '12px' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {stats.topAlbums.length ? (
                        stats.topAlbums.map(([album, count], i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.015)',
                              border: '1px solid rgba(255, 255, 255, 0.03)',
                              marginBottom: '6px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '10px',
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  fontSize: '0.8rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  background:
                                    i === 0
                                      ? 'linear-gradient(135deg, #ffd700, #ffa500)'
                                      : i === 1
                                        ? 'linear-gradient(135deg, #c0c0c0, #a9a9a9)'
                                        : i === 2
                                          ? 'linear-gradient(135deg, #cd7f32, #8b5a2b)'
                                          : 'rgba(255, 255, 255, 0.08)',
                                  color:
                                    i < 3 ? '#000' : 'rgba(255, 255, 255, 0.7)',
                                }}
                              >
                                {i + 1}
                              </span>
                              <span
                                style={{
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'left',
                                }}
                              >
                                {album}
                              </span>
                            </div>
                            <span
                              style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: 'rgba(255, 255, 255, 0.65)',
                                flexShrink: 0,
                              }}
                            >
                              {count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '8px',
                            color: 'rgba(255, 255, 255, 0.45)',
                            fontSize: '0.9rem',
                          }}
                        >
                          No album data found.
                        </div>
                      )}
                    </div>
                  </IonCardContent>
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </>
      )}
    </div>
  );
};

export default LibrarySummaryStep;
